import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Badge from '../../components/common/Badge';
import Spinner from '../../components/common/Spinner';
import { providersAPI } from '../../api/providers';
import { bookingsAPI } from '../../api/bookings';
import { authAPI } from '../../api/auth';
import {
  UserGroupIcon,
  BuildingStorefrontIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatter';

const Card = ({ children, style, ...rest }) => (
  <div
    {...rest}
    className={`dashboard-card min-w-0 ${rest.className || ''}`}
    style={{
      backgroundColor: '#fff',
      border: '1px solid var(--gray-4)',
      borderRadius: '14px',
      padding: 'clamp(14px, 4vw, 20px)',
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionTitle = ({ icon: Icon, children, action }) => (
  <div className="dashboard-section-title flex min-w-0 items-start justify-between gap-3 mb-4">
    <div className="flex min-w-0 items-center gap-2">
      {Icon && (
        <Icon
          className="dashboard-section-title__icon"
          style={{ width: '18px', height: '18px', color: 'var(--orange-9)', flexShrink: 0 }}
        />
      )}
      <h3
        className="text-wrap-safe"
        style={{
          fontSize: 'var(--font-size-small)',
          fontWeight: 600,
          color: 'var(--gray-12)',
          margin: 0,
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {children}
      </h3>
    </div>
    {action}
  </div>
);

const StatCard = ({ label, value, sub, icon: Icon, tint, iconColor }) => (
  <Card className="dashboard-stat-card" style={{ padding: 'clamp(10px, 2.5vw, 14px)' }}>
    <div className="dashboard-stat-card__top flex min-w-0 items-center gap-2 sm:gap-3 mb-1.5 sm:mb-3">
      <div
        className="dashboard-stat-card__icon"
        style={{
          width: 'clamp(26px, 6vw, 32px)',
          height: 'clamp(26px, 6vw, 32px)',
          borderRadius: '8px',
          backgroundColor: tint,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon className="dashboard-stat-card__icon-svg" style={{ width: 'clamp(13px, 3.2vw, 16px)', height: 'clamp(13px, 3.2vw, 16px)', color: iconColor }} />
      </div>
      <span className="dashboard-stat-card__label text-wrap-safe" style={{ fontSize: 'clamp(11px, 2.8vw, 12px)', color: 'var(--gray-10)' }}>{label}</span>
    </div>
    <p
      className="dashboard-stat-card__value text-wrap-safe"
      style={{
        fontSize: 'clamp(17px, 4.5vw, 21px)',
        fontWeight: 600,
        color: 'var(--gray-12)',
        margin: 0,
        textAlign: 'left',
        fontFamily: "'Poppins', sans-serif",
        lineHeight: 1.1,
      }}
    >
      {value}
    </p>
    {sub && (
      <p className="dashboard-stat-card__sub text-wrap-safe" style={{ fontSize: 'clamp(10px, 2.6vw, 11px)', color: 'var(--gray-10)', margin: '3px 0 0', textAlign: 'left' }}>
        {sub}
      </p>
    )}
  </Card>
);

const ActionCard = ({ to, icon: Icon, title, meta }) => (
  <Link
    to={to}
    className="group flex min-w-0 items-center gap-3 rounded-xl border p-3 transition-all sm:p-4"
    style={{
      textDecoration: 'none',
      borderColor: 'var(--gray-4)',
      backgroundColor: 'var(--gray-1)',
      color: 'var(--gray-12)',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = 'var(--orange-5)';
      e.currentTarget.style.backgroundColor = 'var(--orange-2)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = 'var(--gray-4)';
      e.currentTarget.style.backgroundColor = 'var(--gray-1)';
    }}
  >
    <span
      className="flex shrink-0 items-center justify-center"
      style={{
        width: '38px',
        height: '38px',
        borderRadius: '10px',
        backgroundColor: 'var(--orange-3)',
        color: 'var(--orange-10)',
      }}
    >
      <Icon style={{ width: '18px', height: '18px' }} />
    </span>
    <span className="min-w-0">
      <span className="text-wrap-safe block" style={{ fontSize: 'var(--font-size-small)', fontWeight: 600, color: 'var(--gray-12)' }}>
        {title}
      </span>
      <span className="text-wrap-safe block" style={{ fontSize: 'var(--font-size-caption)', color: 'var(--gray-10)', marginTop: '2px' }}>
        {meta}
      </span>
    </span>
  </Link>
);

const getBookingAmount = (booking) =>
  Number(booking.totalAmount || booking.totalPaid || booking.subtotal || booking.totalPrice || booking.amount || booking.price || 0);

const getBookingService = (booking) =>
  booking.service?.name || booking.serviceName || booking.service || 'Service';

const getBookingParties = (booking) => {
  const client = booking.client?.name || booking.customer?.name || booking.user?.name || booking.clientName || booking.client || 'Client';
  const provider = booking.provider?.businessName || booking.provider?.name || booking.providerName || booking.provider || 'Provider';
  return `${client} -> ${provider}`;
};

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProviders: 0,
    pendingProviders: 0,
    totalBookings: 0,
    totalRevenue: 0,
    activeBookings: 0,
  });
  const [recentProviders, setRecentProviders] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // All three sources are real: providers list, all bookings (admin sees
      // everything), and user counts from the auth service.
      const [providersData, bookingsData, userStats] = await Promise.all([
        providersAPI.getAllProviders().catch(() => providersAPI.getProviders()),
        bookingsAPI.getBookings({}).catch(() => []),
        authAPI.adminStats().catch(() => null),
      ]);
      const allProviders = providersData.providers || providersData;
      const pendingCount = allProviders.filter(p => !p.isApproved).length;
      const allBookings = Array.isArray(bookingsData) ? bookingsData : (bookingsData.bookings || []);

      const activeBookings = allBookings.filter(b => ['CONFIRMED', 'PAID'].includes(b.status)).length;
      const completed = allBookings.filter(b => b.status === 'COMPLETED');
      const totalRevenue = completed.reduce((sum, b) => sum + getBookingAmount(b), 0);
      const totalCommission = completed.reduce((sum, b) => sum + Number(b.commission || 0), 0);

      setStats({
        totalUsers: userStats?.totalUsers ?? allProviders.length,
        newUsersThisMonth: userStats?.newThisMonth ?? 0,
        totalProviders: allProviders.length,
        pendingProviders: pendingCount,
        totalBookings: allBookings.length,
        totalRevenue,
        totalCommission,
        activeBookings,
      });

      // Get recent pending providers (last 5)
      setRecentProviders(
        allProviders
          .filter(p => !p.isApproved)
          .slice(0, 5)
      );

      // Get recent bookings (last 5)
      setRecentBookings(
        allBookings.slice(0, 5)
      );
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set mock data on error
      setStats({
        totalUsers: 0,
        totalProviders: 0,
        pendingProviders: 0,
        totalBookings: 0,
        totalRevenue: 0,
        activeBookings: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="dashboard-content w-full min-w-0 px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
        <div className="dashboard-header mb-8 text-center">
          <p className="type-small text-wrap-safe mx-auto max-w-[320px] sm:max-w-none" style={{ color: 'var(--gray-10)', margin: 0, textAlign: 'center' }}>
            Platform overview and management
          </p>
          <h1 className="type-page-title text-wrap-safe mt-2" style={{ color: 'var(--gray-12)', fontWeight: 700, marginBottom: 0, fontFamily: "'Poppins', sans-serif" }}>
            Admin Dashboard
          </h1>
        </div>

        <div className="dashboard-stats-grid grid grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-4 mb-4 sm:mb-6">
          <StatCard label="Total Users" value={stats.totalUsers} sub={`+${stats.newUsersThisMonth || 0} this month`} icon={UserGroupIcon} tint="var(--gray-3)" iconColor="var(--gray-11)" />
          <StatCard label="Total Providers" value={stats.totalProviders} sub={`${stats.pendingProviders} pending`} icon={BuildingStorefrontIcon} tint="var(--orange-3)" iconColor="var(--orange-9)" />
          <StatCard label="Total Bookings" value={stats.totalBookings} sub={`${stats.activeBookings} active`} icon={CalendarIcon} tint="#dcfce7" iconColor="#16a34a" />
          <StatCard label="Total Revenue" value={formatCurrency(stats.totalRevenue)} sub={`${formatCurrency(stats.totalCommission || 0)} commission earned`} icon={CurrencyDollarIcon} tint="var(--orange-3)" iconColor="var(--orange-9)" />
        </div>

        <Card className="mb-6">
          <SectionTitle icon={ChartBarIcon}>Quick Actions</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            <ActionCard to="/admin/providers" icon={BuildingStorefrontIcon} title="Manage Providers" meta={`${stats.pendingProviders} pending`} />
            <ActionCard to="/admin/bookings" icon={CalendarIcon} title="View Bookings" meta={`${stats.activeBookings} active`} />
            <ActionCard to="/admin/analytics" icon={ChartBarIcon} title="Analytics" meta="View reports" />
            <ActionCard to="/admin/users" icon={UserGroupIcon} title="Manage Users" meta={`${stats.totalUsers} total`} />
            <ActionCard to="/admin/categories" icon={TagIcon} title="Categories" meta="Manage service types" />
          </div>
        </Card>

        <div className="dashboard-services-grid grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <SectionTitle
              icon={ClockIcon}
              action={(
                <Link to="/admin/providers" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none', flexShrink: 0 }}>
                  View All
                </Link>
              )}
            >
              Pending Provider Approvals
            </SectionTitle>

            {recentProviders.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--gray-10)' }}>
                <CheckCircleIcon className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--gray-7)' }} />
                <p className="type-small" style={{ margin: 0 }}>No pending approvals</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentProviders.map((provider) => (
                  <div
                    key={provider.id}
                    className="flex min-w-0 flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4"
                    style={{ backgroundColor: 'var(--gray-2)', borderColor: 'var(--gray-4)' }}
                  >
                    <div className="min-w-0 flex-1">
                      <h4 className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>
                        {provider.businessName || provider.name || 'Provider'}
                      </h4>
                      <p className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', color: 'var(--gray-10)', margin: '2px 0 0' }}>{provider.email}</p>
                      <p style={{ fontSize: 'var(--font-size-caption)', color: 'var(--gray-10)', margin: '4px 0 0' }}>
                        Applied {provider.createdAt ? new Date(provider.createdAt).toLocaleDateString() : 'recently'}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {provider.verificationStatus && (
                        <Badge variant="warning" size="small">
                          {provider.verificationStatus.replace('_', ' ')}
                        </Badge>
                      )}
                      <Link
                        to={`/admin/providers/${provider.id}`}
                        style={{ color: 'var(--orange-9)', fontSize: '12px', fontWeight: 600, textDecoration: 'none' }}
                      >
                        Review
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle
              icon={CalendarIcon}
              action={(
                <Link to="/admin/bookings" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none', flexShrink: 0 }}>
                View All
                </Link>
              )}
            >
              Recent Bookings
            </SectionTitle>

            {recentBookings.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--gray-10)' }}>
                <CalendarIcon className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--gray-7)' }} />
                <p className="type-small" style={{ margin: 0 }}>No recent bookings</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex min-w-0 flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4"
                    style={{ backgroundColor: 'var(--gray-2)', borderColor: 'var(--gray-4)' }}
                  >
                    <div className="min-w-0 flex-1">
                      <h4 className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>
                        {getBookingService(booking)}
                      </h4>
                      <p className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', color: 'var(--gray-10)', margin: '2px 0 0' }}>
                        {getBookingParties(booking)}
                      </p>
                      <p style={{ fontSize: 'var(--font-size-caption)', color: 'var(--gray-10)', margin: '4px 0 0' }}>
                        {booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : 'Recently'}
                      </p>
                    </div>
                    <div className="shrink-0 text-left sm:text-right">
                      <p className="text-wrap-safe" style={{ fontSize: 'var(--font-size-small)', fontWeight: 700, color: 'var(--orange-9)', margin: '0 0 4px' }}>
                        {formatCurrency(getBookingAmount(booking))}
                      </p>
                      <Badge variant="warning" size="small">
                        {booking.status || 'PENDING'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
